import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, can } from "@/lib/auth";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import NewJob from "@/pages/NewJob";
import Approvals from "@/pages/Approvals";
import Stores from "@/pages/Stores";
import RepairItems from "@/pages/RepairItems";
import Technicians from "@/pages/Technicians";
import Users from "@/pages/Users";
import Companies from "@/pages/Companies";
import Brands from "@/pages/Brands";
import Skills from "@/pages/Skills";
import Equipment from "@/pages/Equipment";
import StoreDetail from "@/pages/StoreDetail";
import Compliance from "@/pages/Compliance";
import MaintenanceCompanies from "@/pages/MaintenanceCompanies";
import EquipmentDetail from "@/pages/EquipmentDetail";
import Reports from "@/pages/Reports";
import Invoices from "@/pages/Invoices";
import Scheduled from "@/pages/Scheduled";
import Roles from "@/pages/Roles";
import AuditLog from "@/pages/AuditLog";
import Profile from "@/pages/Profile";
import AdminDanger from "@/pages/AdminDanger";
import Register from "@/pages/Register";
import Invites from "@/pages/Invites";
import Tasks from "@/pages/Tasks";
import { ShieldWarning } from "@phosphor-icons/react";
import { Toaster } from "sonner";

function Private({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

// Built-in roles allowed per section (mirrors backend PERMS + sidebar rules).
// Custom roles are gated via their effective permissions matrix.
const BUILTIN_SECTION_ROLES = {
  approvals: ["admin", "general_manager", "district_manager", "area_manager", "maintenance_manager", "store_manager"],
  maintenance_companies: ["admin", "general_manager", "maintenance_manager"],
  brands: ["admin", "general_manager", "maintenance_manager"],
  companies: ["admin", "general_manager", "maintenance_manager"],
  skills: ["admin", "general_manager", "maintenance_manager"],
  users: ["admin", "general_manager", "maintenance_manager"],
  invites: ["admin", "general_manager", "maintenance_manager"],
  roles: ["admin", "general_manager", "maintenance_manager"],
  reports: ["admin", "general_manager", "district_manager", "area_manager", "maintenance_manager", "store_manager"],
  audit_log: ["admin", "general_manager"],
};

function Gated({ section, children }) {
  const { user } = useAuth();
  if (!user) return null;
  const isBuiltin = user.effective?.is_builtin !== false; // default true for built-ins
  // Built-in roles edited by an admin (override present) are gated by their
  // effective permission matrix, mirroring the sidebar behavior.
  const allowed = isBuiltin && !user.effective?.has_override
    ? (!BUILTIN_SECTION_ROLES[section] || BUILTIN_SECTION_ROLES[section].includes(user.role))
    : can(user, section, "read");
  if (allowed) return children;
  return (
    <div className="p-8 max-w-xl mx-auto mt-12" data-testid="route-forbidden">
      <div className="rmms-card p-6 text-center">
        <ShieldWarning size={36} weight="duotone" className="mx-auto text-amber-500 mb-3" />
        <div className="overline text-rose-600">Access denied</div>
        <h2 className="text-xl font-heading font-semibold tracking-tight mt-1">You don't have permission to view this section.</h2>
        <p className="text-sm text-slate-500 mt-2">Contact an admin if you believe this is a mistake. Your role: <span className="font-mono-data">{user.effective?.label || user.role}</span></p>
        <a href="/" className="inline-block mt-4 text-sm text-[#002FA7] hover:underline">← Return to dashboard</a>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/" element={<Private><Gated section="dashboard"><Dashboard /></Gated></Private>} />
          <Route path="/jobs" element={<Private><Gated section="jobs"><Jobs /></Gated></Private>} />
          <Route path="/jobs/new" element={<Private><Gated section="jobs"><NewJob /></Gated></Private>} />
          <Route path="/jobs/:id" element={<Private><Gated section="jobs"><JobDetail /></Gated></Private>} />
          <Route path="/approvals" element={<Private><Gated section="approvals"><Approvals /></Gated></Private>} />
          <Route path="/scheduled" element={<Private><Gated section="calendar"><Scheduled /></Gated></Private>} />
          <Route path="/tasks" element={<Private><Gated section="tasks"><Tasks /></Gated></Private>} />
          <Route path="/invoices" element={<Private><Gated section="invoices"><Invoices /></Gated></Private>} />
          <Route path="/repair-items" element={<Private><Gated section="repair_items"><RepairItems /></Gated></Private>} />
          <Route path="/stores" element={<Private><Gated section="stores"><Stores /></Gated></Private>} />
          <Route path="/stores/:id" element={<Private><Gated section="stores"><StoreDetail /></Gated></Private>} />
          <Route path="/equipment" element={<Private><Gated section="equipment"><Equipment /></Gated></Private>} />
          <Route path="/equipment/:id" element={<Private><Gated section="equipment"><EquipmentDetail /></Gated></Private>} />
          <Route path="/maintenance-companies" element={<Private><Gated section="maintenance_companies"><MaintenanceCompanies /></Gated></Private>} />
          <Route path="/compliance" element={<Private><Gated section="compliance"><Compliance /></Gated></Private>} />
          <Route path="/technicians" element={<Private><Gated section="technicians"><Technicians /></Gated></Private>} />
          <Route path="/users" element={<Private><Gated section="users"><Users /></Gated></Private>} />
          <Route path="/invites" element={<Private><Gated section="invites"><Invites /></Gated></Private>} />
          <Route path="/roles" element={<Private><Gated section="roles"><Roles /></Gated></Private>} />
          <Route path="/audit-log" element={<Private><Gated section="audit_log"><AuditLog /></Gated></Private>} />
          <Route path="/profile" element={<Private><Profile /></Private>} />
          <Route path="/admin/danger" element={<Private><AdminDanger /></Private>} />
          <Route path="/companies" element={<Private><Gated section="companies"><Companies /></Gated></Private>} />
          <Route path="/brands" element={<Private><Gated section="brands"><Brands /></Gated></Private>} />
          <Route path="/skills" element={<Private><Gated section="skills"><Skills /></Gated></Private>} />
          <Route path="/reports" element={<Private><Gated section="reports"><Reports /></Gated></Private>} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}
